import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { User } from '../../types';
import { IMessage } from '../../services/messaging/IMessagingProvider';
import { MessageBubble } from './MessageBubble';
import { ChatInput } from './ChatInput';

interface ChatWindowProps {
  recipient: User;
  currentUserId: string;
  messages: IMessage[];
  onSendMessage: (content: string, mediaData?: { mediaType: string; mediaUrl: string; thumbnailUrl?: string }) => Promise<void>;
  onClose: () => void;
  isOnline: boolean;
}

export const ChatWindow: React.FC<ChatWindowProps> = ({
  recipient,
  currentUserId,
  messages,
  onSendMessage,
  onClose,
  isOnline
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [sending, setSending] = useState(false);
  const prevMessageCountRef = useRef(0);

  // Instant scroll on initial load (before paint so there's no flash)
  useLayoutEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, []);

  // Smooth scroll when new messages arrive
  useEffect(() => {
    const isNewMessage = messages.length > prevMessageCountRef.current;
    prevMessageCountRef.current = messages.length;
    if (isNewMessage) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSend = async (content: string, mediaData?: { mediaType: string; mediaUrl: string; thumbnailUrl?: string }) => {
    setSending(true);
    try {
      await onSendMessage(content, mediaData);
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSending(false);
    }
  };

  const displayName = recipient.displayName || recipient.display_name || recipient.email;
  const avatarUrl = recipient.photoURL || recipient.photo_url;

  return (
    <div className="chat-window" style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      width: '100%',
      position: 'absolute',
      top: 0,
      left: 0,
      background: 'var(--background-default)',
      overflow: 'hidden'
    }}>
      <style>{`
        @media (max-width: 768px) {
          .chat-window .card {
            border-left: none !important;
            border-right: none !important;
          }
        }
      `}</style>
      {/* Header */}
      <div className="card" style={{ borderRadius: '0', borderBottom: '1px solid var(--border-color)', flexShrink: 0 }}>
        <div className="card-content" style={{ padding: '12px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={displayName}
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    objectFit: 'cover'
                  }}
                />
              ) : (
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: 'var(--primary-color)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 'bold'
                }}>
                  {displayName.charAt(0).toUpperCase()}
                </div>
              )}
              <div>
                <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                  {displayName}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: isOnline ? 'var(--success-color)' : 'var(--text-secondary)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <span style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: isOnline ? 'var(--success-color)' : 'var(--text-secondary)'
                  }} />
                  {isOnline ? 'Online' : 'Offline'}
                </div>
              </div>
            </div>
            <button
              className="icon-btn"
              onClick={onClose}
              style={{
                color: 'var(--text-primary)',
                transition: 'all 0.2s ease'
              }}
              title="Close chat"
            >
              <span className="material-icons">close</span>
            </button>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div ref={messagesContainerRef} style={{
        flex: 1,
        overflowY: 'auto',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
      }}>
        {messages.length === 0 ? (
          <div style={{
            textAlign: 'center',
            color: 'var(--text-secondary)',
            marginTop: '32px'
          }}>
            <span className="material-icons" style={{ fontSize: '48px', opacity: 0.3 }}>
              chat_bubble_outline
            </span>
            <div style={{ marginTop: '8px' }}>No messages yet. Start the conversation!</div>
          </div>
        ) : (
          messages.map((message) => (
            <MessageBubble
              key={message.id}
              message={message}
              isOwn={message.senderId === currentUserId}
            />
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div style={{ flexShrink: 0 }}>
        <ChatInput onSend={handleSend} disabled={sending} />
      </div>
    </div>
  );
};
